'use memo';

import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import type { Row } from '@tanstack/react-table';
import { zodValidator } from '@tanstack/zod-adapter';
import { AccordionButton } from '@/components/Button/AccordionButton';
import ActionButton from '@/components/Button/ActionButton';
import { DataTable, DataTablePagination } from '@/components/DataTable';
import Icon from '@/components/Icon/Icon';
import { HStack, Spacer, VStack } from '@/components/Layout';
import StepperHeaderComponent from '@/components/StepperHeader';
import { Text } from '@/components/Typography/Text';
import { DeviceType } from '@/constants/device';
import { defaultNS, useTranslation } from '@/lib/i18n';
import type { CatalogTableRow } from '@/routes/_staff/devices/_list/settings/catalog/-types';
import CatalogSelectionFilterBar from './-components/CatalogSelectionFilterBar';
import { CatalogSelectionSearchSchema } from './-constants';
import { useCatalogSelectionTable } from './-hooks/useCatalogSelectionTable';
import { buildAddInventoryNavParams } from './-utils/buildAddInventoryNavParams';
export const Route = createFileRoute('/_staff/devices/add/catalog/')({
  component: CatalogSelectionPage,
  validateSearch: zodValidator(CatalogSelectionSearchSchema)
});
function CatalogSelectionPage() {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.devices.add.intro.catalog'
  });
  const navigate = useNavigate();
  const {
    table,
    displayState,
    count,
    selectedRow
  } = useCatalogSelectionTable();
  const handleRowClick = (row: Row<CatalogTableRow>) => {
    row.toggleSelected(!row.getIsSelected());
  };
  const handleFillDetails = () => {
    if (!selectedRow) return;
    const navParams = buildAddInventoryNavParams(selectedRow.NAME.id, selectedRow.NAME.genre);
    navigate(navParams);
  };
  return <VStack className="mx-auto w-[1000px] pb-6">
      <Spacer variant="xxl" />
      <StepperHeaderComponent headerTitle={t('title')} />
      <Spacer variant="l" />
      <Text variant="p">{t('description')}</Text>
      <Spacer variant="l" />

      <HStack className="rounded border bg-neutral-fill p-3">
        <CatalogSelectionFilterBar />
      </HStack>
      <Spacer variant="s" />

      <div className="overflow-auto min-h-0 max-h-[500px]">
        <DataTable table={table} displayState={displayState} onRowClick={handleRowClick} isHoverable />
      </div>
      <DataTablePagination table={table} totalCount={count} />

      <Spacer variant="xl" />
      <ActionButton icon="FilePenIcon" className="w-56" onClick={handleFillDetails} disabled={!selectedRow}>
        {t('fillDetails')}
      </ActionButton>

      <Spacer variant="xxl" />
      <CatalogAdditionalOptions />
    </VStack>;
}
function CatalogAdditionalOptions() {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.devices.add.intro.options'
  });
  const navigate = useNavigate();
  const handleSyncIT = () => {
    navigate({
      to: '/devices/external-it-saas'
    });
  };
  return <VStack className="w-full">
      <Text variant="labelSemiLarge">{t('description')}</Text>
      <Spacer size={24} />
      <AccordionButton icon={<Icon iconName="FilePenIcon" text={t('addManually.title')} textColor="text-primary-fill" colorVariant="primary" />} className="w-full">
        <VStack className="gap-2">
          <div className="flex flex-col items-start gap-2 text-neutral-on-fill">
            <Text variant="p">{t('addManually.description')}</Text>
          </div>
          <Link to="/devices/add">
            <ActionButton size="sm" variant="default" icon="ArrowRightIcon" iconPosition="right">
              {t('addManually.button')}
            </ActionButton>
          </Link>
        </VStack>
      </AccordionButton>
      <Spacer size={24} />
      <ActionButton variant="ghost" className="relative w-full justify-start p-0" onClick={handleSyncIT}>
        <HStack className="items-center justify-between w-full">
          <Icon iconName="DatabaseBackup" text={t('syncIT')} textColor="text-primary-fill" colorVariant="primary" />
          <Icon iconName="ChevronRight" className="m-[10px]" />
        </HStack>
      </ActionButton>
      <Spacer size={24} />
      <AccordionButton icon={<Icon iconName="DatabaseBackup" text={t('bulkCsvUpload.title')} textColor="text-primary-fill" colorVariant="primary" />} className="w-full">
        <VStack className="gap-2">
          <div className="flex flex-col items-start gap-2 text-neutral-on-fill">
            <Text variant="p">{t('bulkCsvUpload.description')}</Text>
          </div>
          <Link to="/devices/import-csv/$deviceType" params={{
          deviceType: DeviceType.COMPUTER
        }} hash="upload-file">
            <ActionButton size="sm" variant="default" icon="ArrowRightIcon" iconPosition="right">
              {t('bulkCsvUpload.button')}
            </ActionButton>
          </Link>
        </VStack>
      </AccordionButton>
    </VStack>;
}